import { useState } from 'react';
import { Button } from '@lombok/ui';
import { TextareaField } from '@/components/form';
import { useApprovePayment, useRejectPayment } from '@/data/hooks';

interface PaymentDecisionActionsProps {
  paymentId: string;
  /** The payment's account id — the backend needs it to locate the payment's partition. */
  accountId: string;
  /** Compact layout for list rows (smaller buttons, inline reject box). */
  size?: 'sm' | 'md';
  /** Optional callback after a successful decision (e.g. close a row). */
  onDecided?: () => void;
}

/**
 * Aprobar / Rechazar controls for a PENDING payment (finance/admin review).
 * Reject captures an optional reason via the shared `paymentDecisionSchema`
 * (reason field). These are UX affordances only — the backend re-checks the
 * caller's role and the payment's state on every decision.
 */
export function PaymentDecisionActions({
  paymentId,
  accountId,
  size = 'md',
  onDecided,
}: PaymentDecisionActionsProps) {
  const approve = useApprovePayment(paymentId, accountId);
  const reject = useRejectPayment(paymentId, accountId);
  const [reason, setReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const pending = approve.isPending || reject.isPending;
  const btnSize = size === 'sm' ? 'sm' : 'md';

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <Button
          size={btnSize}
          onClick={() => approve.mutate({}, { onSuccess: onDecided })}
          disabled={pending}
        >
          {approve.isPending ? 'Aprobando…' : 'Aprobar'}
        </Button>
        <Button
          size={btnSize}
          variant="outline"
          onClick={() => setShowReject((v) => !v)}
          disabled={pending}
        >
          Rechazar
        </Button>
      </div>

      {showReject ? (
        <div className="flex flex-col gap-3 rounded-md border border-border p-3">
          <TextareaField
            label="Motivo del rechazo"
            placeholder="Explica por qué se rechaza el pago (opcional)."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowReject(false)} disabled={pending}>
              Cerrar
            </Button>
            <Button
              size="sm"
              onClick={() =>
                reject.mutate(
                  { reason: reason.trim() || undefined },
                  {
                    onSuccess: () => {
                      setShowReject(false);
                      onDecided?.();
                    },
                  },
                )
              }
              disabled={pending}
            >
              {reject.isPending ? 'Rechazando…' : 'Confirmar rechazo'}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
